import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { formatNum } from 'app/utils';
import { ASP_CAMPAIGNS, ASP_YEAR, aspKategorieBg, aspKategorieBorder, AspZuweisung, buildAspPotenzialListe, IAspAgtPotenzial } from './asp.mock.data';
import { AgenturASPModal, agenturAspModalId, AspTeilnahmeIcon, AspPotenzialZelle } from './AgenturASPModal';

export const AspPotentialListe = observer(() => {
	const { uiStore } = useStore();
	const [data, setData] = useState<IAspAgtPotenzial[]>(() => buildAspPotenzialListe());
	const [selected, setSelected] = useState<IAspAgtPotenzial | undefined>();

	const tm = useMemo(() => {
		const tm = new TableModel<IAspAgtPotenzial, number>();
		const cols: ITableColumn<IAspAgtPotenzial, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: (row: TableRow<IAspAgtPotenzial>) => (
				<div>
					<strong>{row.data.agt.name}</strong>
					<br />
					<small className="color-grey">{row.data.agt.agtId}</small>
				</div>
			),
		});

		ASP_CAMPAIGNS.forEach((c, i) => {
			const isGroupStart = i === 0 || ASP_CAMPAIGNS[i - 1].kategorie !== c.kategorie;
			const modifier = `${aspKategorieBg[c.kategorie]} ${isGroupStart ? aspKategorieBorder[c.kategorie] : ''}`;
			cols.push({
				label: c.nameShort,
				path: `potenziale.${c.id}`,
				sortBy: `potenziale.${c.id}`,
				sortType: 'number',
				cellModifier: modifier,
				labelModifier: modifier,
				columnGroup: {
					label: c.kategorie,
					modifier: `${aspKategorieBg[c.kategorie]} ${aspKategorieBorder[c.kategorie]}`,
				},
				render: (row: TableRow<IAspAgtPotenzial>) => <AspPotenzialZelle potenzial={row.data.potenziale[c.id]} isTop={row.data.top5.includes(c.id)} zuweisung={row.data.teilnahmen[c.id]} />,
				agg: [
					{ path: `potenziale.${c.id}`, aggType: AggregrationType.sum },
					{ path: `potenziale.${c.id}`, aggType: AggregrationType.avg, format: (v: number) => formatNum(v) },
				],
			});
		});

		tm.setCols(cols);
		tm.sortBy = 'agt.name';
		tm.sortAsc = true;
		tm.idProperty = 'agt.agtId';
		tm.idType = 'number';
		return tm;
	}, []);

	tm.onRowClick = (row: TableRow<IAspAgtPotenzial>) => {
		setSelected(row.data);
		uiStore.showModal(agenturAspModalId);
	};

	useEffect(() => {
		tm.setRowData(data);
		tm.sort();
	}, [tm, data]);

	// Modal beim Verlassen der Seite schließen
	useEffect(() => {
		return () => uiStore.hideModal(agenturAspModalId);
	}, [uiStore]);

	const onSaveAssignments = (assigned: Record<string, AspZuweisung>) => {
		if (!selected) {
			return;
		}
		setData((prev) => prev.map((x) => (x.agt.agtId === selected.agt.agtId ? { ...x, teilnahmen: assigned } : x)));
	};

	return (
		<div className="table-container">
			<div className="pad-1rem">
				<div className="page-header content">
					<h2>ASP Potentialliste {ASP_YEAR}</h2>
					<small className="color-grey">
						Identifiziertes Kundenpotenzial je Agentur und Kampagne. Die 5 Kampagnen mit dem höchsten Potenzial einer Agentur sind hervorgehoben, <AspTeilnahmeIcon /> zeigt eine bestehende
						Teilnahme. Klick auf eine Zeile öffnet die Zuweisung.
					</small>
				</div>
			</div>
			<Table tm={tm} stickyHeader={true} stickyFooter={true} />
			<AgenturASPModal vm={selected} onSave={onSaveAssignments} />
		</div>
	);
});
